import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  FormGroup,
  Stack,
  Typography
} from '@mui/material'
import React, { useMemo } from 'react'
import { FixedSizeList, type ListChildComponentProps } from 'react-window'

import Icon from 'components/Icon'
import { KubernetesTagLabelSectionListItem } from 'components/services/create/target-selection/kubernetes/KubernetesTagLabelSectionListItem'

import type {
  K8sAutocompleteSearchOption,
  K8sTagLabelOptionSelectionChangeHandler
} from 'components/services/create/target-selection/kubernetes/types'

type VirtualizedItemListData = {
  options: K8sAutocompleteSearchOption[]
  selectedOptionsUIDs: string[]
  onChange: K8sTagLabelOptionSelectionChangeHandler
}

const LIST_SIZE = 8
const ROW_HEIGHT = 50

const OptionRow: React.FC<ListChildComponentProps<VirtualizedItemListData>> = ({
  index,
  style,
  data
}) => {
  const { options, selectedOptionsUIDs, onChange } = data
  const option = options[index]

  if (!option) return null

  return (
    <KubernetesTagLabelSectionListItem
      key={option.uid}
      handleSelectOption={onChange}
      isSelected={selectedOptionsUIDs.includes(option.uid)}
      option={option}
      style={style}
    />
  )
}

export const KubernetesTagLabelSection: React.FC<{
  label: string
  options: K8sAutocompleteSearchOption[]
  selectedOptions: K8sAutocompleteSearchOption[]
  onChange: VirtualizedItemListData['onChange']
}> = ({ label, options, selectedOptions, onChange }) => {
  /*
   * react-window will re-render its options on every observed form update across any field.
   * Without some defensive memoization applied, this will cause performance issues when attempting
   * to update the sister fields within the form. We expose all of this data to each list item instance
   * via the `itemData` prop on the virtualized list component.
   */
  const itemData = useMemo(
    () => ({
      options,
      selectedOptionsUIDs: selectedOptions.map(({ uid }) => uid),
      onChange
    }),
    [options, selectedOptions, onChange]
  )

  const boundedListHeight = Math.min(LIST_SIZE, options.length) * ROW_HEIGHT

  return (
    <Accordion
      disableGutters
      data-test-target-param-section={label}
      data-testid={`kubernetes-tag-label-accordion-${label}`}
      sx={{ padding: 1 }}
    >
      <AccordionSummary
        data-test-target-param-section-summary
        expandIcon={<Icon icon="chevronDown" />}
      >
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
          <Typography color="text.secondary" component="label" role="label">
            {label}
          </Typography>
          <Chip label={options.length} role="status" size="small" />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <FormGroup data-testid="kubernetes-tag-label-virtualized-list-container">
          <FixedSizeList<VirtualizedItemListData>
            height={boundedListHeight}
            itemCount={options.length}
            itemData={itemData}
            itemSize={ROW_HEIGHT}
            width="100%"
          >
            {OptionRow}
          </FixedSizeList>
        </FormGroup>
      </AccordionDetails>
    </Accordion>
  )
}
